"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getDevToolCopy } from "@/lib/dev-tool-copy";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function UrlEncoder() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"encode" | "decode">("encode");
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const dev = getDevToolCopy(locale);
  const ui = getToolUiCopy(locale);
  const encoderTool = getLocalizedTool(
    tools.find((item) => item.id === "url-encoder") ?? tools[0],
    locale
  );
  const decoderTool = getLocalizedTool(
    tools.find((item) => item.id === "url-decoder") ?? tools[0],
    locale
  );
  const activeTool = mode === "encode" ? encoderTool : decoderTool;

  const process = () => {
    try {
      if (mode === "encode") {
        const encoded = encodeURIComponent(input);
        setOutput(encoded);
      } else {
        const decoded = decodeURIComponent(input);
        setOutput(decoded);
      }
    } catch (error) {
      alert(dev.invalidInput);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(output);
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
           <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:link" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{activeTool.name}</p>
            <p className="leading-relaxed">{activeTool.description}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-[15px] bg-gray-100 p-2 border-2 border-gray-200">
        <button
          onClick={() => setMode("encode")}
          className={`rounded-[10px] py-3 text-lg font-bold transition-all flex items-center justify-center gap-2 ${
            mode === "encode"
              ? "bg-white text-positivus-dark shadow-sm border-2 border-positivus-dark"
              : "text-gray-500 hover:text-positivus-dark hover:bg-white/50"
          }`}
        >
          <Icon icon="mdi:link-variant" className="h-5 w-5" />
          {dev.encode}
        </button>
        <button
          onClick={() => setMode("decode")}
          className={`rounded-[10px] py-3 text-lg font-bold transition-all flex items-center justify-center gap-2 ${
            mode === "decode"
              ? "bg-white text-positivus-dark shadow-sm border-2 border-positivus-dark"
              : "text-gray-500 hover:text-positivus-dark hover:bg-white/50"
          }`}
        >
          <Icon icon="mdi:link-variant-off" className="h-5 w-5" />
          {dev.decode}
        </button>
      </div>

      <div className="space-y-8">
        <div>
           <label className="mb-3 block text-lg font-medium text-positivus-dark">
            {mode === "encode" ? dev.urlToEncode : dev.encodedUrlToDecode}
           </label>
           <textarea
             value={input}
             onChange={(e) => setInput(e.target.value)}
             placeholder={mode === "encode" ? "https://example.com/path?param=value with spaces" : "https%3A%2F%2Fexample.com"}
             className="min-h-[150px] w-full rounded-[20px] border-2 border-positivus-dark/20 bg-white p-5 font-mono text-sm focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all placeholder:text-gray-400"
           />
        </div>

        <button
          onClick={process}
          className="w-full rounded-[20px] bg-positivus-dark px-6 py-5 text-xl font-bold text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
        >
          <div className="flex items-center justify-center gap-3">
             <Icon icon="mdi:sync" className="h-6 w-6" />
             <span>{mode === "encode" ? dev.encodeUrl : dev.decodeUrl}</span>
          </div>
        </button>

        {output && (
          <div>
             <div className="mb-3 flex items-center justify-between">
                <label className="text-lg font-bold text-positivus-dark">{dev.outputResult}</label>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 rounded-[10px] bg-positivus-green/20 px-4 py-2 text-sm font-bold text-positivus-dark hover:bg-positivus-green hover:text-white transition-colors"
                >
                  <Icon icon={uiIcons.copy} className="h-4 w-4" />
                  {ui.copy}
                </button>
             </div>
             <div className="min-h-[150px] w-full rounded-[20px] border-2 border-positivus-dark bg-gray-50 p-6 relative group">
                <pre className="whitespace-pre-wrap font-mono text-sm break-all text-positivus-dark">{output}</pre>
             </div>
          </div>
        )}
      </div>
    </div>
  );
}
